import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  BadgeComponent,
  InlineFormSchemaComponent,
  MenuDropDownItemComponent,
  MenuNavBlockComponent,
  MenuToolComponent
} from '@mds/ui';
import { ActiveAppLayer, MenuConfig, MenuItem, MenuOption, MenuService } from '@services/menu.service';
import { map } from 'rxjs/operators';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AppsMenuComponent } from '@components/menus/apps-menu/apps-menu';
import { AppItem, ColorAliases, MenuSection } from '@mds/common';
import { TYPE_COLOR_MAP } from '@pages/profile/utils/constant';
import { AnalyzeService } from '@services/analyze.service';
import { ECODESIGN_BUILDER } from '@services/apps-data';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDialogService } from '@services/global-dialog.service';
import { RouterHelperService } from '@services/router-helper.service';
import _ from 'lodash';
import { ReportListComponent } from '../reports-menu/report-menu.component';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [
    CommonModule,
    MenuNavBlockComponent,
    MenuToolComponent,
    MatMenuModule,
    MdsIconsModule,
    MatTooltipModule,
    MatRadioModule,
    BadgeComponent,
    ReportListComponent,
    MatButtonModule,
    InlineFormSchemaComponent,
    MenuDropDownItemComponent,
    AppsMenuComponent
  ],
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit {
  private globalDialogService = inject(GlobalDialogService);

  icons = MdsIcon;
  advanceImportApps = [];
  activeApp: ActiveAppLayer;
  pinsData: any[] = [];
  activePinCount: number;
  lightGray = ColorAliases.BORDER_UNIVERSAL_INVERTED_STRONG;
  whiteColor = ColorAliases.ON_PRIMARY;
  schema: any;
  menuOptions: MenuSection[] = [];
  readonly ECODESIGN_BUILDER = ECODESIGN_BUILDER;

  @ViewChild('uploadMenu') uploadMenu!: MatMenu;
  @ViewChild('productDetailForAI') productDetailsForAI!: MatMenu;
  @ViewChild('activeApp') selectAppTrigger!: MatMenuTrigger;
  @ViewChild('activeEntity') activeEntityTrigger!: MatMenuTrigger;
  @ViewChild('additionalEntity') additionalEntityTrigger!: MatMenuTrigger;
  @ViewChild('searchRSL') searchRSL!: MatMenu; // TODO refactor this and related
  @ViewChild('moreMenuOptions') moreMenuOptions!: MatMenu;
  uploadMenuDropdown = [
    {
      key: 'dropbox',
      value: 'Dropbox',
      info: 'Upload from Dropbox',
      icon: MdsIcon.DROPBOX
    },
    {
      key: 'sharepoint',
      value: 'SharePoint',
      info: 'Upload from SharePoint',
      icon: MdsIcon.SHAREPOINT
    },
    {
      key: 'upload_local',
      value: 'Select from computer',
      info: 'Select from computer',
      icon: MdsIcon.LAPTOP
    }
  ];
  public chemicalGrading = false;
  closeIcon = MdsIcon.CLOSE;
  closeButtonColor = [ColorAliases.CONTENT_GREY];
  rslPinMenu = {
    mainIcon: {
      icon: MdsIcon.WHEEL_PIN
    },
    rid: '',
    name: 'Select A Restricted Substance',
    subtitle: 'List to Grade By'
  };
  typeColorMap = TYPE_COLOR_MAP;
  private routerHelperService = inject(RouterHelperService);
  private menuService = inject(MenuService);
  private router = inject(Router);
  private eventDelegatorService = inject(EventDelegatorService);
  private analyzeService = inject(AnalyzeService);
  private destroyRef = inject(DestroyRef);
  importApps$ = this.menuService.getImportApps().pipe(
    map((data) => {
      return [
        {
          items: [
            {
              items: data.map((item) => ({
                icon_app: item.icon,
                app_name: item.display_value,
                description_short: item.description,
                data: item
              })),
              title: ''
            }
          ],
          title: 'Import your data',
          limit: 100,
          enableFavouriteButton: false
        }
      ];
    })
  );
  private _menuConfig: MenuConfig;

  get menuConfig() {
    return this._menuConfig;
  }

  @Input() set menuConfig(config: MenuConfig) {
    this._menuConfig = config;
    this.activeApp = config.activeApp as any;
    this.getRslPin();
  }

  ngOnInit(): void {
    this.getAdvanceImportApps();
    this.getPinsData();
  }

  getPinsData() {
    this.eventDelegatorService.on('rsl-pin-selection', (ev) => {
      if (this.pinsData.find((pin) => pin.rsl === ev.data.pin.rsl)) return;
      ev.data.pin.checkbox = true;
      this.pinsData.push(ev.data.pin);
      this.analyzeService.setPins(ev.data.pin, true);
    });
    this.analyzeService
      .getPinsAsObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.pinsData = res;
      });
    this.analyzeService
      .getPinsAsObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        setTimeout(() => {
          this.activePinCount = data.filter((e) => e.checkbox).length;
          this.pinsData = _.cloneDeep(data.filter((e) => e.type === 'display-RSL'));
        }, 400);
      });
  }

  applyPin(pin) {
    this.pinsData.forEach((element) => {
      element.checkbox = pin.pinid === element.pinid;
    });
    this.analyzeService.setPins(pin, true);
  }

  getRslPin() {
    const rid = this.menuConfig?.activeEntity?.rid?.replace('#', '');
    const layer = this.menuConfig?.activeAppOld?.layer;
    this.chemicalGrading = false;
    this.rslPinMenu.rid = rid;
    if (layer === 'chemical_grading') {
      this.chemicalGrading = true;
      this.schema = {
        name: 'RSLPin',
        lines: [],
        formSchema: [
          {
            description: 'Type here...',
            mandatory: false,
            defaultValue: null,
            formtype: 'tile-autocomplete',
            options: `options/${rid}/search/<keyword>?layer=${layer}&pintype=display-RSL`,
            section: 'Outputs',
            title: 'RSL Pin',
            sortlevel: 90,
            fieldtype: 'LINK',
            key: 'rslpin'
          }
        ]
      };
    }
  }

  removePin(pin) {
    this.analyzeService.removePins(pin, true);
  }

  openAppModal(item: AppItem) {
    this.globalDialogService.openApp(item, this.menuConfig.activeEntity);
    this.selectAppTrigger?.closeMenu();
  }

  getMenuReference(menuTrigger: string, action?: MenuItem): MatMenu | null {
    switch (menuTrigger) {
      case 'uploadMenu':
        return this.uploadMenu;
      case 'more':
        return this.productDetailsForAI;
      case 'moreMenu':
        if (action?.menuOptions) {
          this.menuOptions = action.menuOptions;
        }
        return this.moreMenuOptions;
      default:
        return null;
    }
  }

  onSelectedUpload(item: any) {
    this.menuService.setSelectedAction(item);
  }

  onMenuAction(action: any) {
    this.menuService.setSelectedAction(action);
  }

  getAdvanceImportApps() {
    this.menuService.getAdvanceImportApps().subscribe((val: any) => {
      this.advanceImportApps = [
        {
          items: [
            {
              items: val.map((item) => ({
                icon_app: item.image,
                app_name: item.title,
                description_short: item.description,
                data: item
              })),
              title: ''
            }
          ],
          title: 'Search Advanced Importer',
          enableFavouriteButton: false
        }
      ];
    });
  }

  openAdvanceAppModal(item: AppItem) {
    this.router.navigate(['/', 'advance-importer', item.data.rid]);
    this.additionalEntityTrigger?.closeMenu();
  }

  refreshFavoriteApps(): void {
    this.menuService.getFavoriteApps();
  }

  onClickProductDetails(): void {
    this.menuService.emitProductDetails();
  }

  onClickMenuOption(item: MenuOption): void {
    this.menuService.triggerMenuOption(item);
  }

  openMenuItem(menuAction) {
    this.routerHelperService.openMenuItem(menuAction);
  }
}
